package info.rezident.app.data.quiz

import kotlinx.serialization.Serializable

/**
 * Metadata for a medical specialty including description and characteristics
 */
@Serializable
data class SpecialtyMetadata(
    val description: String,
    val characteristics: List<String>
)

/**
 * Repository object containing metadata for all medical specialties.
 * Provides descriptions and characteristics that can be used when displaying
 * specialty recommendations from the cloud function.
 */
object SpecialtyMetadataRepository {

    /**
     * Complete map of specialty names to their metadata
     */
    private val SPECIALTY_METADATA = mapOf(
        // --- 1. Specialități Medicale ---
        "Alergologie & Imunologie Clinică" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi alergiile și bolile sistemului imunitar, de la astm la afecțiuni autoimune complexe.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Imunitate")
        ),
        "Anestezie & Terapie Intensivă" to SpecialtyMetadata(
            description = "Menții funcțiile vitale ale pacienților critici în terapie intensivă și în sala de operație, cu responsabilitate maximă.",
            characteristics = listOf("Medicală", "Spital", "Stres Ridicat")
        ),
        "Boli Infecțioase" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi infecțiile cauzate de agenți patogeni, de la bacterii și viruși la paraziți și fungi.",
            characteristics = listOf("Medicală", "Spital", "Diagnostic")
        ),
        "Cardiologie" to SpecialtyMetadata(
            description = "Previi, diagnostichezi și tratezi bolile inimii și ale vaselor de sânge, adesea prin proceduri intervenționale avansate.",
            characteristics = listOf("Medicală", "Spital", "Procedurală")
        ),
        "Cardiologie Pediatrică" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi afecțiunile cardiace la copii, de la defecte congenitale la aritmii complexe.",
            characteristics = listOf("Medicală", "Copii", "Diagnostic")
        ),
        "Dermatovenerologie" to SpecialtyMetadata(
            description = "Tratezi afecțiunile pielii, părului, unghiilor și bolile cu transmitere sexuală, cu abordare medicală și estetică.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Procedurală")
        ),
        "Diabet, Nutriție & Boli Metabolice" to SpecialtyMetadata(
            description = "Gestionezi diabetul zaharat și tulburările metabolice și nutriționale, construind relații pe termen lung cu pacienții.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Relație Lungă")
        ),
        "Endocrinologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile glandelor endocrine și tulburările hormonale, de la tiroidă la hipofiză.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Hormoni")
        ),
        "Expertiza Medicală a Capacității de Muncă" to SpecialtyMetadata(
            description = "Evaluezi starea de sănătate a persoanelor pentru a determina capacitatea de muncă și gradul de invaliditate.",
            characteristics = listOf("Medicală", "Administrativ", "Comisie")
        ),
        "Farmacologie Clinică" to SpecialtyMetadata(
            description = "Studiezi medicamentele, interacțiunile lor și utilizarea rațională în clinică, la granița dintre cercetare și practică.",
            characteristics = listOf("Medicală", "Cercetare", "Medicamente")
        ),
        "Gastroenterologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile sistemului digestiv prin endoscopie, de la esofag la pancreas.",
            characteristics = listOf("Medicală", "Procedurală", "Spital")
        ),
        "Gastroenterologie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi afecțiunile digestive, hepatice și nutriționale specifice copiilor și adolescenților, cu abordare pediatrică specialized.",
            characteristics = listOf("Medicală", "Copii", "Procedurală")
        ),
        "Genetică Medicală" to SpecialtyMetadata(
            description = "Diagnostichezi bolile genetice, oferi consiliere genetică și interpretezi teste de specialitate.",
            characteristics = listOf("Medicală", "Consiliere", "Diagnostic")
        ),
        "Geriatrie & Gerontologie" to SpecialtyMetadata(
            description = "Asiguri îngrijirea medicală complexă a pacienților vârstnici, abordând afecțiuni cronice și prevenind declinul.",
            characteristics = listOf("Medicală", "Vârstnici", "Relație Lungă")
        ),
        "Hematologie" to SpecialtyMetadata(
            description = "Tratezi bolile sângelui și ale organelor hematopoetice, inclusiv leucemii, limfoame și alte tulburări hematologice.",
            characteristics = listOf("Medicală", "Spital", "Diagnostic")
        ),
        "Medicină de Familie" to SpecialtyMetadata(
            description = "Asiguri asistența medicală primară continuă și complexă pentru pacienți de toate vârstele.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Relație Lungă")
        ),
        "Medicină de Urgență" to SpecialtyMetadata(
            description = "Stabilizezi și tratezi pacienții cu afecțiuni acute și critice, care necesită intervenție imediată.",
            characteristics = listOf("Medicală", "Stres Ridicat", "Acuți")
        ),
        "Medicină Fizică & de Reabilitare" to SpecialtyMetadata(
            description = "Recuperezi pacienții după accidente sau boli, prin fizioterapie, kinetoterapie și managementul durerii.",
            characteristics = listOf("Medicală", "Recuperare", "Echipă")
        ),
        "Medicină Internă" to SpecialtyMetadata(
            description = "Abordezi diagnostica complexă a pacienților cu multiple afecțiuni, fiind \"regina\" specialităților medicale.",
            characteristics = listOf("Medicală", "Diagnostic", "Spital")
        ),
        "Medicina Muncii" to SpecialtyMetadata(
            description = "Evaluezi relația dintre starea de sănătate a angajaților și mediul de muncă, asigurând siguranța la locul de muncă.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Preventiv")
        ),
        "Medicină Sportivă" to SpecialtyMetadata(
            description = "Previi, diagnostichezi și recuperezi leziunile sportivilor, optimizând performanța fizică și revenirea la activitate.",
            characteristics = listOf("Medicală", "Recuperare", "Sportivi")
        ),
        "Nefrologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile rinichilor, inclusiv managementul dializei și al transplantului renal.",
            characteristics = listOf("Medicală", "Spital", "Dializă")
        ),
        "Nefrologie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi afecțiunile renale acute și cronice la copii, inclusiv managementul dializei și al transplantului renal.",
            characteristics = listOf("Medicală", "Copii", "Spital")
        ),
        "Neonatologie" to SpecialtyMetadata(
            description = "Îngrijești nou-născuții, în special pe cei prematuri sau cu afecțiuni critice, în secțiile de terapie intensivă.",
            characteristics = listOf("Medicală", "Copii", "Stres Ridicat")
        ),
        "Neurologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile sistemului nervos central și periferic, de la creier la nervi periferici.",
            characteristics = listOf("Medicală", "Diagnostic", "Spital")
        ),
        "Neurologie Pediatrică" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi afecțiunile neurologice specifice copiilor, de la epilepsie la paralizii cerebrale.",
            characteristics = listOf("Medicală", "Copii", "Diagnostic")
        ),
        "Oncologie Medicală" to SpecialtyMetadata(
            description = "Gestionezi pacienții cu cancer, incluzând chimioterapie, terapii țintite și îngrijire paliativă.",
            characteristics = listOf("Medicală", "Spital", "Empatie")
        ),
        "Oncologie & Hematologie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi cancerul și bolile de sânge la copii, necesitând o abordare complexă și empatică.",
            characteristics = listOf("Medicală", "Copii", "Empatie")
        ),
        "Pediatrie" to SpecialtyMetadata(
            description = "Asiguri asistența medicală complexă (prevenție, diagnostic, tratament) pentru copii, de la naștere la adolescență.",
            characteristics = listOf("Medicală", "Copii", "Preventiv")
        ),
        "Pneumologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile plămânilor și ale căilor respiratorii, de la astm la tuberculoză.",
            characteristics = listOf("Medicală", "Spital", "Procedurală")
        ),
        "Pneumologie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi afecțiunile respiratorii specifice copiilor și adolescenților, de la astm la fibroză chistică și alte boli pulmonare.",
            characteristics = listOf("Medicală", "Copii", "Spital")
        ),
        "Psihiatrie" to SpecialtyMetadata(
            description = "Diagnostichezi, tratezi și previi tulburările de sănătate mintală, emoționale și de comportament.",
            characteristics = listOf("Medicală", "Minte", "Empatie")
        ),
        "Psihiatrie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi tulburările de sănătate mintală specifice copiilor și adolescenților, de la autism la anxietate.",
            characteristics = listOf("Medicală", "Copii", "Minte")
        ),
        "Radioterapie" to SpecialtyMetadata(
            description = "Tratezi cancerul folosind radiații ionizante, planificând și administrând tratamentele pacienților oncologici.",
            characteristics = listOf("Medicală", "Oncologie", "Tehnologie")
        ),
        "Reumatologie" to SpecialtyMetadata(
            description = "Diagnostichezi și tratezi bolile inflamatorii autoimune care afectează articulațiile, mușchii și oasele.",
            characteristics = listOf("Medicală", "Ambulatoriu", "Imunitate")
        ),

        // --- 2. Specialități Chirurgicale ---
        "Chirurgie Cardiovasculară" to SpecialtyMetadata(
            description = "Efectuezi intervenții chirurgicale complexe pe inimă și vasele mari de sânge, cum ar fi bypass și valve.",
            characteristics = listOf("Chirurgicală", "Inimă", "Stres Ridicat")
        ),
        "Chirurgie Generală" to SpecialtyMetadata(
            description = "Efectuezi o gamă largă de intervenții chirurgicale, în special în zona abdominală (apendic, colecist).",
            characteristics = listOf("Chirurgicală", "Abdomen", "Gărzi")
        ),
        "Chirurgie Orală & Maxilo-Facială" to SpecialtyMetadata(
            description = "Tratezi chirurgical afecțiunile feței, gurii și maxilarelor, la granița dintre chirurgie și stomatologie.",
            characteristics = listOf("Chirurgicală", "Față", "Precizie")
        ),
        "Chirurgie Pediatrică" to SpecialtyMetadata(
            description = "Efectuezi intervenții chirurgicale pentru afecțiuni specifice copiilor, de la nou-născuți la adolescenți.",
            characteristics = listOf("Chirurgicală", "Copii", "Precizie")
        ),
        "Chirurgie Plastică, Estetică & Microchirurgie Reconstructivă" to SpecialtyMetadata(
            description = "Restaurezi sau modifici forma și funcția corpului, fie estetic, fie reconstructiv după traume sau arsuri.",
            characteristics = listOf("Chirurgicală", "Precizie", "Estetică")
        ),
        "Chirurgie Toracică" to SpecialtyMetadata(
            description = "Efectuezi intervenții chirurgicale pe organele din torace, în special plămâni, perete toracic și esofag.",
            characteristics = listOf("Chirurgicală", "Plămâni", "Precizie")
        ),
        "Chirurgie Vasculară" to SpecialtyMetadata(
            description = "Tratezi chirurgical bolile vaselor de sânge (artere și vene), exceptând inima și creierul, cu precizie maximă.",
            characteristics = listOf("Chirurgicală", "Vase", "Precizie")
        ),
        "Neurochirurgie" to SpecialtyMetadata(
            description = "Efectuezi intervenții chirurgicale pe sistemul nervos: creier, măduva spinării și nervi periferici.",
            characteristics = listOf("Chirurgicală", "Creier/Coloană", "Stres Ridicat")
        ),
        "Obstetrică-Ginecologie" to SpecialtyMetadata(
            description = "Supraveghezi sarcina și nașterea (Obstetrică) și tratezi afecțiunile sistemului reproductiv feminin (Ginecologie).",
            characteristics = listOf("Chirurgicală", "Femei", "Gărzi")
        ),
        "Oftalmologie" to SpecialtyMetadata(
            description = "Diagnostichezi, tratezi și efectuezi intervenții chirurgicale pentru afecțiunile ochilor și ale vederii.",
            characteristics = listOf("Chirurgicală", "Ochi", "Precizie")
        ),
        "Ortopedie Pediatrică" to SpecialtyMetadata(
            description = "Tratezi chirurgical și non-chirurgical afecțiunile musculo-scheletale la copii (scolioză, fracturi).",
            characteristics = listOf("Chirurgicală", "Copii", "Oase")
        ),
        "Ortopedie & Traumatologie" to SpecialtyMetadata(
            description = "Tratezi chirurgical fracturile, leziunile și afecțiunile sistemului musculo-scheletal (oase, articulații).",
            characteristics = listOf("Chirurgicală", "Oase", "Gărzi")
        ),
        "Otorinolaringologie" to SpecialtyMetadata(
            description = "Tratezi medicală și chirurgical afecțiunile urechii, nasului și gâtului (ORL), cu abordare complexă și multidisciplinară.",
            characteristics = listOf("Chirurgicală", "ORL", "Ambulatoriu")
        ),
        "Urologie" to SpecialtyMetadata(
            description = "Tratezi medicală și chirurgical afecțiunile sistemului urinar (la ambele sexe) și reproductiv masculin.",
            characteristics = listOf("Chirurgicală", "Rinichi/Prostată", "Procedurală")
        ),

        // --- 3. Specialități Paraclinice ---
        "Anatomie Patologică" to SpecialtyMetadata(
            description = "Diagnostichezi bolile (în special cancer) prin examinarea la microscop a țesuturilor și celulelor pentru a determina natura afecțiunilor.",
            characteristics = listOf("Paraclinică", "Microscop", "Diagnostic")
        ),
        "Epidemiologie" to SpecialtyMetadata(
            description = "Studiezi distribuția și factorii determinanți ai bolilor în populații, esențială pentru sănătatea publică.",
            characteristics = listOf("Paraclinică", "Populație", "Cercetare")
        ),
        "Igienă" to SpecialtyMetadata(
            description = "Promovezi sănătatea și previi bolile prin controlul factorilor de mediu (apă, aer, alimente) și educație sanitară.",
            characteristics = listOf("Paraclinică", "Preventiv", "Mediu")
        ),
        "Medicină de Laborator" to SpecialtyMetadata(
            description = "Supraveghezi și interpretezi analizele medicale (biochimie, hematologie) pentru diagnosticarea bolilor.",
            characteristics = listOf("Paraclinică", "Analize", "Tehnologie")
        ),
        "Medicină Legală" to SpecialtyMetadata(
            description = "Aplici cunoștințele medicale pentru a rezolva probleme juridice, incluzând autopsii și expertize.",
            characteristics = listOf("Paraclinică", "Justiție", "Autopsie")
        ),
        "Medicină Nucleară" to SpecialtyMetadata(
            description = "Utilizezi substanțe radioactive (trasori) atât pentru diagnosticarea (PET-CT), cât și pentru tratarea bolilor și tumori.",
            characteristics = listOf("Paraclinică", "Imagini", "Radiații")
        ),
        "Microbiologie Medicală" to SpecialtyMetadata(
            description = "Identifici microorganismele (bacterii, viruși) care cauzează boli și determini sensibilitatea la tratament.",
            characteristics = listOf("Paraclinică", "Laborator", "Diagnostic")
        ),
        "Radiologie-Imagistică Medicală" to SpecialtyMetadata(
            description = "Interpretezi imaginile medicale (CT, RMN, Ecografie, Radiografie) pentru a diagnostica afecțiunile.",
            characteristics = listOf("Paraclinică", "Imagini", "Diagnostic")
        ),
        "Sănătate Publică & Management" to SpecialtyMetadata(
            description = "Administrezi sistemele de sănătate, creezi politici și gestionezi spitalele sau programele de sănătate.",
            characteristics = listOf("Paraclinică", "Management", "Sistem")
        )
    )

    /**
     * Get description for a specific specialty by name
     * @param specialtyName The name of the specialty
     * @return The description if found, null otherwise
     */
    fun getDescription(specialtyName: String): String? {
        return SPECIALTY_METADATA[specialtyName]?.description
    }

    /**
     * Get characteristics for a specific specialty by name
     * @param specialtyName The name of the specialty
     * @return The list of characteristics if found, empty list otherwise
     */
    fun getCharacteristics(specialtyName: String): List<String> {
        return SPECIALTY_METADATA[specialtyName]?.characteristics ?: emptyList()
    }
}
